/* oxlint-disable unicorn/catch-error-name */
import { service } from '@ember/service';

import * as Sentry from '@sentry/ember';
import { dropTask, timeout } from 'ember-concurrency';
import Base from 'ember-simple-auth/authenticators/base';

import ENV from 'register-partners-js/config/environment';
import { authBaseURL, authUserNamespace } from 'register-partners-js/constants/hosts';
import { HTTP_STATUS_CODES } from 'register-partners-js/constants/http-status-codes';

const REFRESH_LEEWAY = 5 * 60 * 1000;
export const SESSION_URL = `${authBaseURL}/${authUserNamespace}/users/sessions`;

function getTimeLeft(expires) {
  return expires ? Date.parse(expires) - Date.now() : -1;
}

export default class CustomAuthenticator extends Base {
  @service networkManager;

  async restore(sessionData) {
    if (!sessionData.expires) {
      throw new Error('No session data');
    }

    let timeLeft = getTimeLeft(sessionData.expires);
    if (timeLeft <= 0) {
      throw new Error('Session expired');
    } else if (timeLeft < REFRESH_LEEWAY) {
      return await this.refreshAccessToken();
    } else {
      this._scheduleRefreshTask.perform(timeLeft - REFRESH_LEEWAY);
      return sessionData;
    }
  }

  async invalidate() {
    try {
      await this.networkManager.request(SESSION_URL, { method: 'DELETE' });
    } catch (error) {
      // we ignore all errors because throwing an error here could lead to
      // an infinite session invalidation loop

      // 401 is an expected failure after a session expiry timeout so we ignore
      // that without reporting it to Sentry, all other issues are reported to
      // Sentry.

      if (error.status !== HTTP_STATUS_CODES.UNAUTHORIZED) {
        Sentry.withScope(scope => {
          scope.setLevel('warning');
          Sentry.captureException(new Error('Session invalidation failed'));
        });
      }
    }
  }

  async authenticate({ email, password, code }) {
    let method = email ? 'POST' : 'PUT';
    let data = JSON.stringify({ user: { email, password, code } });

    // we're explicitly destructuring here because we don't want to save the
    // full response (including the `user` model) in the session data cookie
    // since the cookie size is limited
    let { expires, websocket_token } = await this.networkManager.request(SESSION_URL, {
      method,
      body: data,
      dataType: 'json',
      credentials: 'include',
    });

    let timeLeft = getTimeLeft(expires);
    if (timeLeft > REFRESH_LEEWAY) {
      this._scheduleRefreshTask.perform(timeLeft - REFRESH_LEEWAY);
    }

    return { expires, websocket_token };
  }

  async refreshAccessToken() {
    try {
      let sessionData = await this.networkManager.request(SESSION_URL, { method: 'PUT' });

      this.trigger('sessionDataUpdated', sessionData);

      let timeLeft = getTimeLeft(sessionData.expires);
      if (timeLeft > REFRESH_LEEWAY) {
        this._scheduleRefreshTask.perform(timeLeft - REFRESH_LEEWAY);
      }

      return sessionData;
    } catch (err) {
      if (
        err.status === HTTP_STATUS_CODES.UNAUTHORIZED ||
        err.status === HTTP_STATUS_CODES.FORBIDDEN
      ) {
        await this.invalidate();
        this.trigger('sessionDataInvalidated');
      } else {
        throw err;
      }
    }
  }

  _scheduleRefreshTask = dropTask(async timeToWait => {
    if (ENV.environment !== 'test') {
      await timeout(timeToWait);
      this.refreshAccessToken();
    }
  });
}
